import { useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDebouncedCallback } from 'use-debounce';

import { StoreAutoComplete } from 'components/autocompletes';
import { CissTextField } from 'components/fields';
import { Filter } from 'components/filter';
import { FilterValuesProps } from 'hooks/useFilter';
import { RequestOptionsType } from 'hooks/useRequestListagem';
import { GerarReembolsoPromocaoAutoComplete, GerarReembolsoStatusAutoComplete } from 'pages/financeiro/reembolso/gerar-reembolso/components';

export function GerarReembolsoGridFilter() {
    const [requestOptionsPromocao, setRequestOptionsPromocao] = useState<RequestOptionsType | null>(null);

    const [filterLoja, setFilterLoja] = useState<FilterValuesProps>({
        idProperty: 'cdLoja',
        property: 'cdLojas',
        operator: 'in',
        value: [],
    });

    const [filterPromocoes, setFilterPromocoes] = useState<FilterValuesProps>({
        idProperty: 'idPromocao',
        property: 'idPromocoes',
        operator: 'in',
        value: [],
    });

    const [filterStatus, setFilterStatus] = useState<FilterValuesProps>({
        idProperty: 'idStatusApuracao',
        property: 'idStatusApuracao',
        operator: 'eq',
        value: null,
        required: 'Status é obrigatório.',
    });

    const [filterCdApuracao, setFilterCdApuracao] = useState<FilterValuesProps>({
        property: 'cdApuracaoFormatado',
        operator: 'like',
        value: null,
    });

    const [filterDtInicio, setFilterDtInicio] = useState<FilterValuesProps>({
        property: 'dtInicio',
        operator: 'eq',
        value: null,
        required: 'Data de início é obrigatória.',
    });

    const [filterDtFim, setFilterDtFim] = useState<FilterValuesProps>({
        property: 'dtFim',
        operator: 'eq',
        value: null,
        required: 'Data de fim é obrigatória.',
    });

    const isValidDates = isValid(filterDtInicio?.value) && isValid(filterDtFim?.value);

    /**
     * Atualiza as opções de requisição para o autocomplete de promoções com base no intervalo de datas selecionado
     */
    const updateRequestOptionsPromocao = useDebouncedCallback(() => {
        if (isValidDates) {
            setRequestOptionsPromocao({
                url: '/financeiro/promocao/reembolso/periodo',
                sort: [{ property: 'idPromocao', direction: 'ASC' }],
                filter: [
                    { property: 'dtInicio', operator: 'gte', value: format(filterDtInicio.value, 'MM/dd/yyyy') },
                    { property: 'dtFim', operator: 'lte', value: format(filterDtFim.value, 'MM/dd/yyyy') },
                ],
            });
        } else {
            setRequestOptionsPromocao(null);
        }
    }, 300);

    useEffect(updateRequestOptionsPromocao, [filterDtInicio.value, filterDtFim.value]);

    return (
        <Filter
            preventQueryParamsEmptyStart
            filters={[
                { value: filterLoja, setter: setFilterLoja },
                { value: filterCdApuracao, setter: setFilterCdApuracao },
                { value: filterDtInicio, setter: setFilterDtInicio },
                { value: filterDtFim, setter: setFilterDtFim },
                { value: filterPromocoes, setter: setFilterPromocoes },
                { value: filterStatus, setter: setFilterStatus },
            ]}
        >
            <Grid sx={{ width: '100%' }} size={{ lg: 6 }}>
                <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns} localeText={{ start: 'Data de início', end: 'Data de fim' }}>
                    <DateRangePicker
                        value={[filterDtInicio.value ?? null, filterDtFim.value ?? null]}
                        maxDate={new Date()}
                        onChange={([startDate, endDate]) => {
                            setFilterDtInicio((oldState: FilterValuesProps) => ({ ...oldState, value: startDate }));
                            setFilterDtFim((oldState: FilterValuesProps) => ({ ...oldState, value: endDate }));
                            setFilterPromocoes((oldState: FilterValuesProps) => ({ ...oldState, value: [] }));
                        }}
                        slots={{ field: MultiInputDateRangeField }}
                        slotProps={{
                            field: { dateSeparator: 'até' },
                            textField: { autoComplete: 'off', size: 'small' },
                        }}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ lg: 6 }}>
                <GerarReembolsoStatusAutoComplete
                    value={filterStatus.value}
                    onChange={(e, value) => setFilterStatus((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>
            <Grid size={{ lg: 4 }}>
                <GerarReembolsoPromocaoAutoComplete
                    multiple
                    size="small"
                    value={filterPromocoes.value}
                    disabled={!isValidDates}
                    onChange={(e, value) => setFilterPromocoes((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    requestOptions={requestOptionsPromocao}
                />
            </Grid>
            <Grid size={{ lg: 4 }}>
                <StoreAutoComplete
                    multiple
                    size="small"
                    defaultStore={false}
                    importStores={false}
                    value={filterLoja.value}
                    onChange={(e, value) => setFilterLoja((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>
            <Grid size={{ lg: 4 }}>
                <CissTextField
                    size="small"
                    label="Código da apuração"
                    value={filterCdApuracao.value}
                    onChange={(e) => setFilterCdApuracao((oldState: FilterValuesProps) => ({ ...oldState, value: e.target.value }))}
                />
            </Grid>
        </Filter>
    );
}
